import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { print } from "graphql";
import { createClient, ExecutionResult } from "graphql-ws";
import { useEffect, useState } from "react";
import { Observable, Subscription } from "rxjs";
import { createApiRequest, StrictUndef } from "shared/api/httpClient";
import { useOIDCStore } from "shared/stores/oidc";
import { maybe } from "shared/utils/maybe";
import { match, P } from "ts-pattern";

export type GqlVar = Record<string, unknown>;

class GraphqlError extends Error {
    constructor(readonly messages: string[]) {
        super(`[GraphQL Error] ${messages.join(" | ")}`);
    }
}

type GqlTransportOptions = {
    headers: () => Record<string, string | number | null>;
    onUnauthenticated?: () => void;
};

export type GqlTransport = {
    <T, V>(doc: TypedDocumentNode<T, V>, variables?: V, init?: RequestInit): Promise<StrictUndef<NonNullable<ExecutionResult<T>["data"]>>>;

    stream<T, V extends GqlVar>(doc: TypedDocumentNode<T, V>, variables?: V): Observable<StrictUndef<T>>;
    useStream<T, V extends GqlVar>(doc: TypedDocumentNode<T, V>, variables?: V): StrictUndef<T> | undefined;
    useStreamNotNull<T, V extends GqlVar>(doc: TypedDocumentNode<T, V>, variables: V | null): StrictUndef<T> | undefined;
};

/** @private */
function createTransport({ headers, onUnauthenticated }: GqlTransportOptions): GqlTransport {
    const socket = createClient({
        url: import.meta.env["VITE_API_BASE_URL"] + "/subscriptions",
        shouldRetry: () => true,
        connectionParams: () => headers(),
    });

    async function exec<T, V>(
        doc: TypedDocumentNode<T, V>,
        variables?: V,
        init?: RequestInit,
    ): Promise<StrictUndef<NonNullable<ExecutionResult<T>["data"]>>> {
        const body = JSON.stringify({
            query: typeof document === "string" ? document : print(doc),
            variables: (variables ?? {}) as V,
        });

        const result = await createApiRequest<ExecutionResult<T>>("/graphql", {
            method: "POST",
            headers: { "Content-Type": "application/json", ...headers() },
            body,
            ...init,
        });

        if (result.errors?.length) {
            if (result.errors.some((it) => it.message.includes("Not Authenticated"))) {
                onUnauthenticated?.();
            } else {
                throw new GraphqlError(result.errors.map((e) => e.message));
            }
        }

        if (!result.data) {
            throw new GraphqlError(["No data"]);
        }

        return result.data as StrictUndef<typeof result.data>;
    }

    return Object.assign(exec, {
        /** Creates an {@link Observable} bound to the result of the given graphql subscription document */
        stream<T, V extends Record<string, unknown>>(doc: TypedDocumentNode<T, V>, variables?: V): Observable<StrictUndef<T>> {
            return new Observable((observer) => {
                const dispose = socket.subscribe(
                    { query: print(doc), variables },
                    {
                        next: ({ data }) => observer.next(data as StrictUndef<T>),
                        error: (err) => observer.error(err),
                        complete: () => observer.complete(),
                    },
                );
                return () => dispose();
            });
        },

        useStream<T, V extends GqlVar>(doc: TypedDocumentNode<T, V>, variables?: V) {
            const [subscription, setSubscription] = useState<Subscription>();
            const [lastValue, setLastValue] = useState<StrictUndef<T>>();

            useEffect(() => {
                setSubscription(
                    gqlx.stream(doc, variables).subscribe({
                        next(value) {
                            setLastValue(value);
                        },
                    }),
                );

                return () => subscription?.unsubscribe();
            }, [JSON.stringify(variables)]);

            return lastValue;
        },

        useStreamNotNull<T, V extends GqlVar>(doc: TypedDocumentNode<T, V>, variables: V | null) {
            const [subscription, setSubscription] = useState<Subscription | null>(null);
            const [lastValue, setLastValue] = useState<StrictUndef<T>>();

            useEffect(() => {
                if (variables == null) {
                    setSubscription(null);
                    return;
                }

                setSubscription(
                    gqlx.stream(doc, variables).subscribe({
                        next(value) {
                            setLastValue(value);
                        },
                    }),
                );

                return () => subscription?.unsubscribe();
            }, [JSON.stringify(variables)]);

            return lastValue;
        },
    });
}

export const gqlx = createTransport({
    headers() {
        return {
            ...match(import.meta.env["VITE_CUSTOMER_ID_OVERRIDE"])
                .with(P.string, (id) => ({ "customer-id": id }))
                .otherwise(() => ({})),
            ...match(import.meta.env["VITE_USER_ID_OVERRIDE"])
                .with(P.nullish, () =>
                    maybe(useOIDCStore.getState().getAccessToken())?.take((token) => ({
                        Authorization: "Bearer " + token,
                    })),
                )
                .otherwise((it) => ({ ["user-id"]: it })),
        };
    },
    onUnauthenticated() {
        window.location.href = "/logout";
    },
});

export const optInGqlx = (userId?: string) =>
    createTransport({
        headers() {
            return {
                ["opt-in"]: userId ?? null,
            };
        },
        onUnauthenticated() {
            window.location.href = "/opt-in";
        },
    });
